import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/issues/components/IssuesToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { RootState } from '@/setup/redux/RootReducer';
import axiosInstance from '@/setup/axios/axiosInstance';

import { createDefaultIssueForm, issueStatusOptions } from '../issuesMockData';
import { InventoryIssueFormData, InventoryIssueFormLine, InventoryIssueRow } from '../types';
import { createIssue, updateIssue, getIssueById } from '../redux/IssueCRUD';
import useRequestMaterialWarehouses from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialWarehouses';
import useRequestMaterialItems from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialItems';
import useRequestMaterialEmployees from '@/modules/inventory/requestMaterial/hooks/useRequestMaterialEmployees';
import { getOrders } from '@/modules/orders/redux/OrderCRUD';

import type { TransactionStatusOption } from '@/modules/finance/utils/transactionStatusOptions';
import IssueForm from './IssueForm';
import { uploadIssueDocument } from '@/modules/documents/redux/DocumentCRUD';
import IssueOverviewList, { IssueOverviewListHandle } from './IssueOverviewList';
import IssueNeedExportList, { IssueNeedExportListHandle } from './IssueNeedExportList';
import { formatErrorForSnackbar, toApiError } from '@/__core/helpers/Error';

const buildFormMetadata = (formData: InventoryIssueFormData, issueId?: string) => ({ formData, issueId });

const hasIssueChanges = (
  current: InventoryIssueFormData,
  updates: Partial<InventoryIssueFormData>,
) => Object.entries(updates).some(([field, value]) => {
  const currentValue = (current as any)[field];
  return typeof value === 'object'
    ? JSON.stringify(value) !== JSON.stringify(currentValue)
    : value !== currentValue;
});

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveCompanyIdForQuery = (companyId?: string) => {
  if (!companyId || companyId === 'all' || companyId === 'global' || !UUID_PATTERN.test(companyId)) {
    return '';
  }
  return companyId;
};

const isListTabType = (type?: string) =>
  type === 'issue-list' || type === 'need-export-list';

interface StockBalanceSnapshot {
  on_hand_qty: number;
  allocated_qty: number;
  available_qty: number;
  last_price?: number;
  uom_name?: string;
}

interface IssueItemUomOption {
  id: string;
  name: string;
}

const extractIssueItemUomOptions = (stockBalanceRecord: any): IssueItemUomOption[] => {
  const options = new Map<string, IssueItemUomOption>();

  const appendOption = (candidate: any) => {
    const id = String(candidate?.id || candidate?.uom_id || candidate?.to_uom_id || candidate?.to_unit_id || '').trim();
    if (!id || options.has(id)) {
      return;
    }

    const name = String(
      candidate?.name ||
      candidate?.uom_name ||
      candidate?.title ||
      candidate?.code ||
      candidate?.uom?.name ||
      candidate?.to_uom?.name ||
      candidate?.to_unit_name ||
      candidate?.to_unit_code ||
      '',
    ).trim();

    options.set(id, { id, name: name || id });
  };

  appendOption(stockBalanceRecord?.uom);
  (stockBalanceRecord?.uom?.conversions || []).forEach((conversion: any) => {
    appendOption(conversion?.to_uom);
    appendOption(conversion?.to_unit);
    appendOption({
      id: conversion?.to_uom_id,
      name: conversion?.to_uom_name,
    });
    appendOption({
      id: conversion?.to_unit_id,
      name: conversion?.to_unit_name || conversion?.to_unit_code,
    });
  });

  return Array.from(options.values());
};

export default function IssuesModule() {
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const issueOverviewListRef = useRef<IssueOverviewListHandle>(null);
  const needExportListRef = useRef<IssueNeedExportListHandle>(null);
  const defaultStatusRef = useRef<{ id: string; name: string }>({ id: 'draft', name: 'Nháp' });

  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', status: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dirtyTabIds, setDirtyTabIds] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [apiIssueStatuses, setApiIssueStatuses] = useState<TransactionStatusOption[]>([]);

  const formStatusOptions = useMemo(() => {
    const source = apiIssueStatuses.length > 0 ? apiIssueStatuses : issueStatusOptions;
    return source
      .filter((option) => option.id !== 'all')
      .map((option) => ({ id: option.id, name: option.name, code: (option as any).code || '', color: (option as any).color || '', is_default: (option as any).is_default || false }));
  }, [apiIssueStatuses]);

  const toolbarStatusOptions = useMemo(() => {
    if (apiIssueStatuses.length > 0) {
      return [
        { id: 'all', name: 'Tất cả' },
        ...apiIssueStatuses.map((option) => ({ id: option.id, name: option.name })),
      ];
    }
    return issueStatusOptions;
  }, [apiIssueStatuses]);

  const selectedCompanyId = useMemo(
    () => String(selectedCompany?.company_id || getCurrentCompanyId() || '').trim(),
    [selectedCompany?.company_id],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const listScopeCompanyId = useMemo(
    () => resolveCompanyIdForQuery(activeCompanyFilter),
    [activeCompanyFilter],
  );

  const listCompanyId = listScopeCompanyId || selectedCompanyId;

  const { canCreate: canCreateIssues, resolveTabPermissions, resolvePermissionsForCompanyId, canReadRecord } = useModuleToolbarPermissions({
    resource: 'inventory.issue',
    activeCompanyFilter,
    updateTabTypes: ['update-issue'],
  });

  const { resolvePermissionsForCompanyId: resolveIssueItemPermissionsForCompanyId } = useModuleToolbarPermissions({
    resource: 'inventory.item',
    activeCompanyFilter,
  });

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 99, title: 'Danh sách phiếu', type: 'issue-list', closable: false, createdAt: Date.now() },
      { id: 0, title: 'Danh sách cần xuất', type: 'need-export-list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateIssues) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateIssues]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_issues_tabs_v5',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateIssues),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-issue', 'update-issue'],
    allowMultipleTabTypes: ['new-issue', 'update-issue'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = listScopeCompanyId || selectedCompanyId;
      const defaultForm = createDefaultIssueForm(baseCompanyId);
      defaultForm.status = defaultStatusRef.current.id;
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `PX #${currentTabs.filter((tab) => tab.type === 'new-issue').length + 1}`,
        type: 'new-issue',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(defaultForm),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryIssueFormData | undefined;
  const activeIssueId = (
    activeTabData?.metadata?.issueId
    || activeTabData?.entityId
  ) as string | undefined;

  const effectiveFormStatusOptions = useMemo(() => {
    const normalizedOptions = formStatusOptions.filter((option) => Boolean(option.id));
    if (!activeFormData?.status || String(activeFormData.status).trim() === '') {
      return normalizedOptions;
    }
    if (normalizedOptions.some((option) => option.id === activeFormData.status || option.code === activeFormData.status)) {
      return normalizedOptions;
    }
    return [
      ...normalizedOptions,
      { id: String(activeFormData.status), name: String(activeFormData.status), code: '', color: '' },
    ];
  }, [activeFormData?.status, formStatusOptions]);

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/settings/statuses/by-module/', { params: { module: 'issue' } })
      .then((response) => {
        if (cancelled) return;
        const root = response.data?.data ?? response.data;
        const payload = root?.issue ?? root;
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.results)
            ? payload.results
            : Array.isArray(payload?.items)
              ? payload.items
              : Array.isArray(payload?.groups)
                ? payload.groups.flatMap((group: any) => Array.isArray(group?.items) ? group.items : [])
                : [];
        setApiIssueStatuses(items.map((item: any) => ({
          id: String(item.id ?? ''),
          name: item.name || item.code || String(item.id ?? ''),
          code: item.code ? String(item.code) : undefined,
          color: item.color || undefined,
          is_default: Boolean(item.is_default),
          is_active: item.is_active !== false,
        })).filter((item: TransactionStatusOption) => item.id && item.is_active !== false));
        const defaultStatus = items.find((item: any) => item.is_default);
        if (defaultStatus) {
          defaultStatusRef.current = { id: String(defaultStatus.id), name: defaultStatus.name || defaultStatus.code || String(defaultStatus.id) };
        }
      })
      .catch(() => {
        if (!cancelled) setApiIssueStatuses([]);
      });
    return () => { cancelled = true; };
  }, [activeFormData?.company_id]);

  const { warehouseOptions } = useRequestMaterialWarehouses({
    companyId: activeFormData?.company_id || '',
    skip: !activeFormData?.company_id,
  });

  const { employeeOptions, currentEmployeeId } = useRequestMaterialEmployees({
    companyId: activeFormData?.company_id || '',
    userId: currentUser?.id ? String(currentUser.id) : '',
    skip: !activeFormData?.company_id,
  });

  useEffect(() => {
    if (!activeTabData?.id || !activeFormData || activeFormData.issued_by_id || !currentEmployeeId) {
      return;
    }

    updateTab(activeTabData.id, {
      metadata: buildFormMetadata({
        ...activeFormData,
        issued_by_id: currentEmployeeId,
        received_by_id: currentEmployeeId,
      }, activeIssueId),
    });
  }, [activeFormData, activeIssueId, activeTabData?.id, updateTab, currentEmployeeId]);

  useEffect(() => {
    if (!activeTabData?.id || !activeFormData || employeeOptions.length === 0) return;

    const defaultEmployee = employeeOptions.find((e) => e.id === activeFormData.issued_by_id);
    if (!defaultEmployee || activeFormData.issued_by_name === defaultEmployee.name) return;

    updateTab(activeTabData.id, {
      metadata: buildFormMetadata({
        ...activeFormData,
        issued_by_name: defaultEmployee.name,
        received_by_name: defaultEmployee.name,
      }, activeIssueId),
    });
  }, [activeFormData?.issued_by_id, activeIssueId, activeTabData?.id, updateTab, employeeOptions]);

  useEffect(() => {
    if (!activeTabData?.id || !activeFormData || activeFormData.store_id || warehouseOptions.length === 0) {
      return;
    }

    const defaultWarehouse = warehouseOptions.find((warehouse) => warehouse.is_default);
    if (!defaultWarehouse) {
      return;
    }

    updateTab(activeTabData.id, {
      metadata: buildFormMetadata({
        ...activeFormData,
        store_id: defaultWarehouse.id,
        store_name: defaultWarehouse.name,
      }, activeIssueId),
    });
  }, [activeFormData, activeIssueId, activeTabData?.id, updateTab, warehouseOptions]);

  const { itemOptions, loading: itemLoading, searchLoading: itemSearchLoading, searchItems: searchItemOptions, addOption: addItemOption } = useRequestMaterialItems({
    companyId: activeFormData?.company_id || '',
    skip: !activeFormData?.company_id,
  });

  const [uomOptionsByLineId, setUomOptionsByLineId] = useState<Record<number, IssueItemUomOption[]>>({});
  const [stockByLineId, setStockByLineId] = useState<Record<number, StockBalanceSnapshot | null>>({});
  const [stockLoadingByLineId, setStockLoadingByLineId] = useState<Record<number, boolean>>({});
  const [stockErrorByLineId, setStockErrorByLineId] = useState<Record<number, boolean>>({});

  const [orderOptions, setOrderOptions] = useState<any[]>([]);
  const [orderLoading, setOrderLoading] = useState(false);

  useEffect(() => {
    const companyId = activeFormData?.company_id;
    if (!companyId || companyId === 'global') {
      setOrderOptions([]);
      return;
    }
    let cancelled = false;
    setOrderLoading(true);
    getOrders({ company: companyId, page_size: 200 })
      .then((response) => {
        if (!cancelled) {
          const results = response.data?.results || response.data || [];
          setOrderOptions(Array.isArray(results) ? results : []);
        }
      })
      .catch(() => {
        if (!cancelled) setOrderOptions([]);
      })
      .finally(() => {
        if (!cancelled) setOrderLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeFormData?.company_id]);

  const stockLinesKey = useMemo(() => {
    return (activeFormData?.lines || []).map(line => `${line.item_id}__${line.uom_id}`).join('|');
  }, [activeFormData?.lines]);

  useEffect(() => {
    const companyId = activeFormData?.company_id;
    const storeId = activeFormData?.store_id;
    const currentLines = activeFormData?.lines || [];
    const fallbackUomOptionsByLineId = currentLines.reduce<Record<number, IssueItemUomOption[]>>((accumulator, line) => {
      const matchedItem = itemOptions.find((item) => item.id === line.item_id);
      const fallbackUomId = line.uom_id || matchedItem?.unit_id || '';
      const fallbackUomName = line.uom_name || matchedItem?.unit_name || fallbackUomId;
      accumulator[line.id] = fallbackUomId ? [{ id: fallbackUomId, name: fallbackUomName }] : [];
      return accumulator;
    }, {});

    if (!companyId || !storeId || currentLines.length === 0) {
      setStockByLineId({});
      setStockLoadingByLineId({});
      setStockErrorByLineId({});
      setUomOptionsByLineId(fallbackUomOptionsByLineId);
      return;
    }

    const itemRequests = currentLines.map((line) => {
      const matchedItem = itemOptions.find((item) => item.id === line.item_id);
      return {
        lineId: line.id,
        itemId: line.item_id || matchedItem?.id || '',
        itemCode: (line.item_code || matchedItem?.code || '').trim(),
        uomId: line.uom_id || matchedItem?.unit_id || '',
        fallbackUomName: line.uom_name || matchedItem?.unit_name || '',
        fallbackUomOptions: fallbackUomOptionsByLineId[line.id] || [],
      };
    });

    const loadingState = itemRequests.reduce<Record<number, boolean>>((accumulator, current) => {
      if (current.itemCode && current.uomId) {
        accumulator[current.lineId] = true;
      }
      return accumulator;
    }, {});

    setUomOptionsByLineId(fallbackUomOptionsByLineId);
    setStockLoadingByLineId(loadingState);

    let cancelled = false;

    void (async () => {
      const uniqueRequests = Array.from(new Map(
        itemRequests
          .filter((request) => request.itemId && request.uomId)
          .map((request) => [`${request.itemId}__${request.uomId}`, request]),
      ).values());

      const responseByKey = new Map<string, { snapshot: StockBalanceSnapshot | null; uomOptions: IssueItemUomOption[]; error: boolean }>();

      await Promise.all(uniqueRequests.map(async (request) => {
        const requestKey = `${request.itemId}__${request.uomId}`;

        try {
          const response = await axiosInstance.get('/inventory/stock-balances/', {
            params: {
              item_id: request.itemId,
              store_id: storeId,
            },
          });

          const records = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.results)
              ? response.data.results
              : response.data ? [response.data] : [];
          const rawRecord = records.find((record: any) =>
            String(record?.item_id || '') === String(request.itemId)
            && (!record?.store_id || String(record.store_id) === String(storeId))
          ) || records[0];
          const responseUomOptions = rawRecord ? extractIssueItemUomOptions(rawRecord) : [];
          const summedStock = records.reduce((total: { on_hand_qty: number; allocated_qty: number; available_qty: number }, record: any) => ({
            on_hand_qty: total.on_hand_qty + (Number(record?.on_hand_qty) || 0),
            allocated_qty: total.allocated_qty + (Number(record?.allocated_qty) || 0),
            available_qty: total.available_qty + (Number(record?.available_qty) || 0),
          }), { on_hand_qty: 0, allocated_qty: 0, available_qty: 0 });

          responseByKey.set(requestKey, {
            snapshot: rawRecord
              ? {
                ...summedStock,
                last_price: Number(rawRecord.last_price) || 0,
                uom_name: rawRecord.uom_name || rawRecord.uom?.name || request.fallbackUomName,
              }
              : {
                on_hand_qty: 0,
                allocated_qty: 0,
                available_qty: 0,
                last_price: 0,
                uom_name: request.fallbackUomName,
              },
            uomOptions: responseUomOptions.length > 0 ? responseUomOptions : request.fallbackUomOptions,
            error: false,
          });
        } catch (_error) {
          responseByKey.set(requestKey, {
            snapshot: null,
            uomOptions: request.fallbackUomOptions,
            error: true,
          });
        }
      }));

      if (cancelled) {
        return;
      }

      const nextStockByLineId: Record<number, StockBalanceSnapshot | null> = {};
      const nextErrorByLineId: Record<number, boolean> = {};
      const nextUomOptionsByLineId: Record<number, IssueItemUomOption[]> = {};

      itemRequests.forEach((request) => {
        if (!request.itemCode || !request.uomId) {
          nextStockByLineId[request.lineId] = null;
          nextErrorByLineId[request.lineId] = false;
          nextUomOptionsByLineId[request.lineId] = request.fallbackUomOptions;
          return;
        }

        const resolved = responseByKey.get(`${request.itemId}__${request.uomId}`);
        nextStockByLineId[request.lineId] = resolved?.snapshot || null;
        nextErrorByLineId[request.lineId] = resolved?.error || false;
        nextUomOptionsByLineId[request.lineId] = resolved?.uomOptions || request.fallbackUomOptions;
      });

      setStockByLineId(nextStockByLineId);
      setStockErrorByLineId(nextErrorByLineId);
      setUomOptionsByLineId(nextUomOptionsByLineId);
      setStockLoadingByLineId({});
    })();

    return () => {
      cancelled = true;
    };
  }, [activeFormData?.company_id, activeFormData?.store_id, stockLinesKey, itemOptions]);

  const refreshActiveList = useCallback(
    (options?: { showSuccessMessage?: boolean }) => {
      if (activeTabData?.type === 'issue-list') {
        issueOverviewListRef.current?.requestRefresh(options);
        return;
      }
      if (activeTabData?.type === 'need-export-list') {
        needExportListRef.current?.requestRefresh(options);
      }
    },
    [activeTabData?.type],
  );

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const updateFormData = (updates: Partial<InventoryIssueFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeIssueId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    if (!hasIssueChanges(activeFormData, updates)) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const openExistingIssue = async (row: InventoryIssueRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết phiếu xuất.', severity: 'error' });
      return;
    }

    if (detailLoading) return;

    setDetailLoading(true);
    let fullRow: InventoryIssueRow;
    try {
      fullRow = await getIssueById(row.id);
    } catch {
      fullRow = row;
    } finally {
      setDetailLoading(false);
    }

    guardAndRun(() => {
      const statusId = typeof fullRow.status === 'object' && fullRow.status !== null && fullRow.status.id
        ? fullRow.status.id
        : (fullRow.status_code || '');

      openTab({
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: fullRow.code,
        type: 'update-issue',
        closable: true,
        entityId: fullRow.id,
        metadata: {
          companyId: fullRow.company_id,
          ...buildFormMetadata(
          {
            code: fullRow.code,
            name: fullRow.name,
            order_no: fullRow.order_no,
            order_id: fullRow.order_id || '',
            store_id: fullRow.warehouse_id || '',
            store_name: fullRow.store_name,
            status: statusId,
            status_is_posted: typeof fullRow.status === 'object' && fullRow.status !== null && fullRow.status.is_posted === true,
            amount: fullRow.amount ? String(fullRow.amount) : '',
            company_id: fullRow.company_id,
            note: fullRow.note || '',
            issued_at: fullRow.issued_at || null,
            issued_by_id: fullRow.issued_by_id || '',
            issued_by_name: fullRow.issued_by_name || '',
            received_by_id: fullRow.received_by_id || '',
            received_by_name: fullRow.received_by_name || '',
            lines: (fullRow.lines || []).map((line, index) => ({
              id: index + 1,
              item_id: line.item_id,
              item_code: line.item_code,
              item_name: line.item_name,
              uom_id: line.uom_id,
              uom_name: line.uom_name,
              issued_qty: line.issued_qty,
              unit_cost: line.unit_cost || 0,
              line_amount: line.line_amount || 0,
              note: line.note,
            })),
          },
          fullRow.id
        ),
        },
      });
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const handleRefresh = () => {
    refreshActiveList({ showSuccessMessage: true });
  };

  const handleChangeLine = (lineId: number, updates: Partial<InventoryIssueFormLine>) => {
    if (!activeTabData?.id || !activeFormData) return;
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = Boolean(activeIssueId) ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) return;

    const currentLines = activeFormData.lines || [];
    const nextLines = currentLines.map((line) =>
      line.id === lineId ? { ...line, ...updates } : line,
    );
    if (!hasIssueChanges(activeFormData, { lines: nextLines })) {
      return;
    }
    const nextFormData = { ...activeFormData, lines: nextLines };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const handleAddLine = () => {
    if (!activeTabData?.id || !activeFormData) return;
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = Boolean(activeIssueId) ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) return;

    const currentLines = activeFormData.lines || [];
    const maxId = currentLines.reduce((max, line) => Math.max(max, line.id), 0);
    const newLine: InventoryIssueFormLine = {
      id: maxId + 1,
      item_code: '',
      item_name: '',
      uom_name: '',
      issued_qty: 0,
      unit_cost: 0,
      line_amount: 0,
      note: '',
    };
    const nextFormData = { ...activeFormData, lines: [...currentLines, newLine] };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const handleRemoveLine = (lineId: number) => {
    if (!activeTabData?.id || !activeFormData) return;
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = Boolean(activeIssueId) ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) return;

    const currentLines = activeFormData.lines || [];
    const nextLines = currentLines.filter((line) => line.id !== lineId);
    const nextFormData = { ...activeFormData, lines: nextLines };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeIssueId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
    setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: true }));
  };

  const resolveStatusId = (statusValue: string, statusOptions: Array<{ id: string; name: string }>) => {
    if (!statusValue) return '';
    const matched = statusOptions.find((option) => option.id === statusValue);
    return matched?.id || statusValue;
  };

  const handleSave = async (files: File[] = []): Promise<string | false> => {
    if (!activeFormData || !activeTabData?.id || saving) return false;

    const isUpdateMode = Boolean(activeIssueId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật phiếu xuất.', severity: 'error' });
      return false;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo phiếu xuất.', severity: 'error' });
      return false;
    }

    const normalizedStatusId = resolveStatusId(activeFormData.status, formStatusOptions);
    if (!normalizedStatusId) {
      setSnackbarMessage({ message: 'Không xác định được trạng thái hợp lệ để lưu phiếu xuất.', severity: 'error' });
      return false;
    }

    const lines = (activeFormData.lines || []).map((line) => ({
      item: line.item_id || '',
      uom: line.uom_id || '',
      issued_qty: line.issued_qty || 0,
      unit_cost: Number(line.unit_cost) || Number(stockByLineId[line.id]?.last_price) || 0,
      note: line.note || '',
    }));

    const payload = {
      company: activeFormData.company_id,
      warehouse: activeFormData.store_id,
      status: normalizedStatusId,
      order: activeFormData.order_id || undefined,
      content: activeFormData.name || '',
      issued_at: activeFormData.issued_at || new Date().toISOString(),
      note: activeFormData.note || '',
      issued_by_id: activeFormData.issued_by_id || undefined,
      issued_by_name: activeFormData.issued_by_name || undefined,
      received_by_id: activeFormData.received_by_id || undefined,
      received_by_name: activeFormData.received_by_name || undefined,
      lines,
    };

    setSaving(true);
    try {
      let saved: any;
      if (isUpdateMode && activeIssueId) {
        saved = await updateIssue({ ...payload, id: activeIssueId });
      } else {
        saved = await createIssue(payload);
      }

      const nextId = saved?.id || activeIssueId;
      let attachmentUploadFailed = false;
      if (nextId && files.length > 0) {
        try {
          await Promise.all(files.map((file) => uploadIssueDocument(nextId, file)));
        } catch {
          attachmentUploadFailed = true;
        }
      }

      issueOverviewListRef.current?.requestRefresh();
      needExportListRef.current?.requestRefresh();
      handleUnsavedChangesUpdate(false, activeTabData.id);
      setDirtyTabIds((current) => ({ ...current, [activeTabData.id]: false }));
      setSnackbarMessage({
        message: attachmentUploadFailed
          ? `Đã lưu ${saved?.issue_no || activeFormData.code || activeFormData.name}, nhưng upload file đính kèm thất bại.`
          : `Đã lưu ${saved?.issue_no || activeFormData.code || activeFormData.name}${files.length > 0 ? ' và file đính kèm.' : '.'}`,
        severity: attachmentUploadFailed ? 'error' : 'success',
      });
      const savedStatus = typeof saved?.status === 'object' && saved.status !== null ? saved.status : null;
      const savedFormData = {
        ...activeFormData,
        code: saved?.issue_no || activeFormData.code,
        name: saved?.content || activeFormData.name,
        status: savedStatus?.id || activeFormData.status,
        status_is_posted: savedStatus ? savedStatus.is_posted === true : activeFormData.status_is_posted === true,
      };
      updateTab(activeTabData.id, {
        title: saved?.issue_no || activeFormData.code || activeFormData.name,
        entityId: nextId,
        metadata: buildFormMetadata(savedFormData, nextId),
      });
      return nextId || false;
    } catch (err: any) {
      const detail = formatErrorForSnackbar(toApiError(err)) || 'Có lỗi xảy ra khi lưu phiếu xuất.';
      setSnackbarMessage({ message: detail, severity: 'error' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  

  const renderListContent = (tabType: string) => {
    if (tabType === 'issue-list') {
      return (
        <IssueOverviewList
          ref={issueOverviewListRef}
          companyId={listCompanyId}
          searchQuery={searchQuery}
          status={filters.status}
          onSelectRow={openExistingIssue}
          onFetchError={(message) => setSnackbarMessage({ message, severity: 'error' })}
          onRefreshSuccess={() => setSnackbarMessage({ message: 'Đã làm mới danh sách phiếu.', severity: 'success' })}
        />
      );
    }

    return (
      <IssueNeedExportList
        ref={needExportListRef}
        companyId={listCompanyId}
        searchQuery={searchQuery}
        status={filters.status}
        onSelectRow={openExistingIssue}
        onFetchError={(message) => setSnackbarMessage({ message, severity: 'error' })}
      />
    );
  };

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryIssueFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);
              const canCreateItemForTab = resolveIssueItemPermissionsForCompanyId(tabFormData?.company_id || '').canCreate;

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {isListTabType(tab.type) ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={toolbarStatusOptions}
                            onRefresh={handleRefresh}
                          />
                        }
                        dataTable={renderListContent(tab.type)}
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <IssueForm
                        formData={tabFormData}
                        issueId={(tab.metadata?.issueId || tab.entityId) as string | undefined}
                        statusOptions={effectiveFormStatusOptions}
                        warehouseOptions={warehouseOptions}
                        orderOptions={orderOptions}
                        orderLoading={orderLoading}
                        itemOptions={itemOptions}
                        uomOptionsByLineId={uomOptionsByLineId}
                        itemLoading={itemLoading}
                        employeeOptions={employeeOptions}
                        stockByLineId={stockByLineId}
                        stockLoadingByLineId={stockLoadingByLineId}
                        stockErrorByLineId={stockErrorByLineId}
                        onChangeForm={(updates) => {
                          const canEditTab = tab.type === 'new-issue' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            if (!hasIssueChanges(tabFormData, updates)) {
                              return;
                            }
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.issueId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            setDirtyTabIds((current) => ({ ...current, [tab.id]: true }));
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={handleSave}
                        saving={saving}
                        onChangeLine={handleChangeLine}
                        onAddLine={handleAddLine}
                        onRemoveLine={handleRemoveLine}
                        canEdit={tab.type === 'new-issue' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        onCreateItem={addItemOption}
                        canCreateItem={canCreateItemForTab}
                        searchItems={searchItemOptions}
                        searchLoading={itemSearchLoading}
                        isDirty={!!dirtyTabIds[tab.id]}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert
          severity={snackbarMessage?.severity || 'success'}
          onClose={() => setSnackbarMessage(null)}
          sx={{ width: '100%', whiteSpace: 'pre-line' }}
        >
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
